import { useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { nanoid } from 'nanoid';
import {
  UserTrackingServiceApi,
  V1Platform,
  type V1ExperimentLookupRequest,
} from '@flink/experiment';
import { useLocation } from 'react-router-dom';
import { useCountry } from './useCountry';
import { FLAGS, WATERFALL_APPLY_FORM } from '../constants/flags';
import { buildFlagAttributes } from '../helpers/flagAttributes';
import { isLanguageRoute } from '../constants/routes';
import { useFlagsStore } from '../store/flags';
import { type Decisions, type FlagDefinition } from '../types/experimentation';

const api = new UserTrackingServiceApi(
  {},
  `${process.env.REACT_APP_API_BASE_URL}/user-tracking-gateway/user-tracking-gateway`,
);

// TODO: update the localStorage key name once existing A/B tests on Optimizely are concluded
const USER_ID_KEY = 'optimizelyUserId';

// Written once, before any hook reads it, so every hook instance sees the same id on first render.
function ensureUserId(): void {
  try {
    if (!localStorage.getItem(USER_ID_KEY)) {
      localStorage.setItem(USER_ID_KEY, JSON.stringify(nanoid(11)));
    }
  } catch {
    // storage unavailable: the hook falls back to an empty id
  }
}

export function useUniqueUserId() {
  ensureUserId();
  const [uniqueUserId] = useLocalStorage(USER_ID_KEY, '');
  return uniqueUserId;
}

export const useFlagDecisions = () => {
  const userId = useUniqueUserId();
  const country = useCountry();
  const existingDecisions = useFlagsStore((state) => state.decisions);
  const setDecisions = useFlagsStore((state) => state.setDecisions);

  useEffect(() => {
    const body: V1ExperimentLookupRequest = {
      userTrackingId: '',
      userId,
      meta: {
        attributes: {
          country_iso: country,
        },
      },
      flags: [...Object.values(FLAGS).map((flag) => flag.experimentKey)],
      platform: V1Platform.OptimizelyLastMile,
    };

    async function fetchExperiment() {
      const result = await api.userTrackingServiceExperimentLookup(body);
      const decisions = result.data.decisions as unknown as Decisions;
      setDecisions(decisions);

      console.log('fetchExperiment: ', decisions);
    }

    if (!existingDecisions) {
      fetchExperiment();
    }
  }, [userId, country, existingDecisions]);
};

export function eppoLookupKey(pathname: string, attributes: Record<string, string>): string {
  return `${pathname}?${new URLSearchParams(attributes).toString()}`;
}

type UseFlagValueResult = {
  settled: boolean;
  pending: boolean;
  value: string | null;
};

export function useApplyFormConfig(enabled: boolean): UseFlagValueResult {
  const userId = useUniqueUserId();
  const { pathname, search } = useLocation();
  const startEppoLookup = useFlagsStore((state) => state.startEppoLookup);
  const setEppoDecisions = useFlagsStore((state) => state.setEppoDecisions);
  const { eppoDecisions, eppoSettled, eppoKey } = useFlagsStore();

  const attributes = useMemo(() => buildFlagAttributes({ pathname, search }), [pathname, search]);
  const key = enabled && isLanguageRoute(pathname) ? eppoLookupKey(pathname, attributes) : null;

  useEffect(() => {
    if (!key || !userId) return;
    const requestId = startEppoLookup(key);
    if (requestId === null) return;
    const body: V1ExperimentLookupRequest = {
      userTrackingId: userId,
      userId,
      meta: { attributes },
      flags: [WATERFALL_APPLY_FORM.experimentKey],
      platform: V1Platform.EppoOps,
    };
    api
      .userTrackingServiceExperimentLookup(body)
      .then((result) => setEppoDecisions(requestId, result.data.decisions as unknown as Decisions))
      .catch(() => setEppoDecisions(requestId, null));
  }, [key, attributes, userId, startEppoLookup, setEppoDecisions]);

  const settled = key !== null && eppoSettled && eppoKey === key;
  const decision = settled ? eppoDecisions?.[WATERFALL_APPLY_FORM.experimentKey] : undefined;
  return { settled, pending: key !== null && !settled, value: decision?.variation ?? null };
}

type UseFlagResult = {
  enabled: boolean;
  isClientReady: boolean;
};

export function useFlagResult(flag: FlagDefinition): UseFlagResult {
  const result = { isClientReady: false, enabled: false };
  const { decisions } = useFlagsStore();

  if (decisions === null) {
    return result;
  }

  const decision = decisions[flag.experimentKey];

  result.isClientReady = true;
  if (decision) {
    result.enabled = decision.variation === flag.variation;
  }

  return result;
}
